/**
 * Pure view model for one member's rounds sheet (ISSUE-1318).
 *
 * The sheet copies the legacy player sheet: who it is, their status, a row of
 * season numbers, the teams they have used, then their picks round by round.
 * Every number here is the server's. `/results/get-by-member` sends status,
 * points, medals, used teams and rounds; the standings row the sheet was
 * opened from adds wins and losses. Nothing here counts picks to make a
 * number of its own, because that would be a second copy of grading.
 *
 * `/results/get-by-member` already returns locked picks only, so a pick that
 * has not locked is never in this data and never needs filtering here.
 */
import type {
  typeSurvivorRound,
  typeSurvivorRoundEntry,
} from "./SurvivorRoundList";

export const MEMBER_ROUNDS_LOADING_LABEL = "Loading picks";
export const MEMBER_ROUNDS_FAILED_MESSAGE = "We could not load these picks.";
export const MEMBER_ROUNDS_RETRY_LABEL = "Try again";
export const MEMBER_ROUNDS_DESCRIPTION =
  "Picks show here once they lock, so nobody sees a pick before kickoff.";
export const MEMBER_ROUNDS_TEAMS_USED_TITLE = "Teams used";
export const MEMBER_ROUNDS_PICKS_TITLE = "Picks by round";
export const MEMBER_ROUNDS_CLOSE_LABEL = "Close";
export const MEMBER_ROUNDS_STAT_WINS = "Wins";
export const MEMBER_ROUNDS_STAT_LOSSES = "Losses";
export const MEMBER_ROUNDS_STAT_POINTS = "Points";
export const MEMBER_ROUNDS_STAT_MEDALS = "Medals";
/** An unknown number reads as a dash, never as a guessed zero. */
export const MEMBER_ROUNDS_UNKNOWN_STAT = "-";

export type typeMemberRoundsWireEntry = {
  entryId: string;
  label: string;
  status: "in" | "eliminated" | "revived";
  pointsTotal: number;
  medalsThisContest: number;
  usedTeamIds: readonly string[];
  roundsPicked: readonly typeSurvivorRound[];
};

export type typeMemberRoundsStat = { label: string; value: string };

export type typeMemberRoundsView = {
  /** The entries the round list draws. One when the sheet opened on a row. */
  entries: typeSurvivorRoundEntry[];
  status: typeMemberRoundsWireEntry["status"] | null;
  stats: typeMemberRoundsStat[];
  usedTeamIds: string[];
};

function statValue(value: number | null | undefined): string {
  return value === null || value === undefined
    ? MEMBER_ROUNDS_UNKNOWN_STAT
    : String(value);
}

/**
 * Narrows to the entry the sheet was opened from when it is in the answer.
 * A member with two entries who was opened from one row sees that entry, the
 * way the legacy sheet highlights the row it came from. Opened from anywhere
 * else (no `focusEntryId`, or an id the answer does not carry), every entry
 * shows, and the numbers add up across them.
 */
export function buildMemberRoundsView(input: {
  entries: readonly typeMemberRoundsWireEntry[];
  focusEntryId: string | null;
  wins: number | null | undefined;
  losses: number | null | undefined;
}): typeMemberRoundsView {
  const focused =
    input.focusEntryId === null
      ? []
      : input.entries.filter((entry) => entry.entryId === input.focusEntryId);
  const shown = focused.length > 0 ? focused : [...input.entries];

  const points = shown.reduce((sum, entry) => sum + entry.pointsTotal, 0);
  const medals = shown.reduce((sum, entry) => sum + entry.medalsThisContest, 0);
  const usedTeamIds = [
    ...new Set(shown.flatMap((entry) => [...entry.usedTeamIds])),
  ];

  // Wins and losses come off one standings row, so they only describe the
  // board when exactly that row's entry is the one on screen.
  const isOneRow = focused.length === 1;

  return {
    entries: shown.map((entry) => ({
      entryId: entry.entryId,
      label: entry.label,
      rounds: entry.roundsPicked,
    })),
    status: shown.length === 1 ? (shown[0]?.status ?? null) : null,
    stats: [
      {
        label: MEMBER_ROUNDS_STAT_WINS,
        value: statValue(isOneRow ? input.wins : null),
      },
      {
        label: MEMBER_ROUNDS_STAT_LOSSES,
        value: statValue(isOneRow ? input.losses : null),
      },
      {
        label: MEMBER_ROUNDS_STAT_POINTS,
        value: shown.length === 0 ? MEMBER_ROUNDS_UNKNOWN_STAT : String(points),
      },
      {
        label: MEMBER_ROUNDS_STAT_MEDALS,
        value: shown.length === 0 ? MEMBER_ROUNDS_UNKNOWN_STAT : String(medals),
      },
    ],
    usedTeamIds,
  };
}
